/* ============================================================================
   js-amperage.js
   JS Alternators, "Which one do I need?" amperage pop-up. Stage 1.
   Added September 2026.

   Pairs with assets/js-amperage.css and snippets/js-amperage-popup.liquid.

   WHAT IT DOES
   The snippet writes a small JSON block into the page holding only the
   amperage tiers this product actually sells, with that product's own
   prices. This file reads it and draws the bars.

   THE TWO RULES THIS ENFORCES, both Justin's

   1. NEVER SHOW AN AMPERAGE THE CUSTOMER CANNOT BUY on the page they are
      looking at. Because the tier list comes from the product's own
      variants, a product that only offers 250 and 320 physically cannot
      render a 370 row. There is no master list in here to fall out of step
      with the catalogue.

   2. NEVER POINT THEM AT A DIFFERENT VEHICLE. "Someone shopping a Silverado
      has no use for a Sierra link." Where the page tops out below what they
      need, we cap at the highest tier on that page, say so, and offer the
      dual or triple alternator conversation. See buildBars, the overcap
      block at the end.

   Where Justin's 9 Sep mockup file and these rules disagree, the rules win.
   He said so himself: the mockup was built on the Silverado, which is one of
   the minority of products offering all four tiers.

   NO JQUERY ON PURPOSE. The theme loads jQuery 3.3.1 from a CDN, which has
   known security advisories. Nothing here needs it, so nothing here uses it.
============================================================================ */

(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';
  var LOCK_CLASS = 'jsamp-lock';


  /* ----------------------------------------------------------- utilities */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function num(n) {
    try { return Number(n).toLocaleString('en-US'); }
    catch (e) { return String(n); }
  }

  /* Reads the JSON the snippet wrote for this instance. Returns null rather
     than throwing, so a malformed block disables the pop-up instead of
     breaking the rest of the product page. */
  function readData(scrim) {
    var id = scrim.getAttribute('data-jsamp-data');
    if (!id) return null;
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      var d = JSON.parse(el.textContent);
      if (!d || !d.tiers || !d.tiers.length) return null;
      return d;
    } catch (e) {
      if (window.console) console.warn('[js-amperage] could not parse tier data', e);
      return null;
    }
  }

  /* ------------------------------------------------- which tier is chosen */

  /* Finds the amperage currently selected on the page, so the pop-up opens
     with the right bar highlighted. Falls back to the smallest tier. */
  function currentAmp(data) {
    var wrap = document.querySelector('[data-jsamp-scope]') || document;

    /* 1. The billet option cards, if they are on this page. */
    var card = wrap.querySelector('.billet-amp[aria-checked="true"], [data-billet-amp][aria-checked="true"]');
    if (card) {
      var fromCard = parseInt(card.getAttribute('data-amp') || card.textContent, 10);
      if (fromCard) return fromCard;
    }

    /* 2. The theme's own variant radios. */
    var radio = wrap.querySelector('.variant-input input[type="radio"]:checked');
    if (radio && radio.value) {
      var fromRadio = parseInt(radio.value, 10);
      if (fromRadio) return fromRadio;
    }

    /* 3. The theme's variant dropdown. */
    var sel = wrap.querySelector('.variant__input--dropdown, select[data-index="option1"]');
    if (sel && sel.value) {
      var fromSel = parseInt(sel.value, 10);
      if (fromSel) return fromSel;
    }

    return data.tiers[0].amp;
  }

  /* Sets the amperage on the page when someone taps a bar. Tries each
     control the theme might be using. If none of them match, it does
     nothing at all rather than guessing, and the pop-up just closes. */
  function selectAmpOnPage(amp, data) {
    var tier = null;
    for (var i = 0; i < data.tiers.length; i++) {
      if (data.tiers[i].amp === amp) { tier = data.tiers[i]; break; }
    }
    if (!tier) return false;

    var raw = tier.value; /* the real option value, e.g. "250 amp alternator" */

    /* Billet option cards. */
    var cards = $$('[data-billet-amp], .billet-amp');
    for (var c = 0; c < cards.length; c++) {
      var cv = cards[c].getAttribute('data-value') || cards[c].getAttribute('data-amp') || '';
      if (cv === raw || parseInt(cv, 10) === amp) { cards[c].click(); return true; }
    }

    /* Variant radios. */
    var radios = $$('.variant-input input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      if (radios[r].value === raw) {
        radios[r].checked = true;
        radios[r].dispatchEvent(new Event('change', { bubbles: true }));
        var label = radios[r].closest('.variant-input');
        if (label) { var lbl = label.querySelector('label'); if (lbl) lbl.click(); }
        return true;
      }
    }

    /* Variant dropdown. */
    var selects = $$('select');
    for (var s = 0; s < selects.length; s++) {
      var opts = selects[s].options;
      for (var o = 0; o < opts.length; o++) {
        if (opts[o].value === raw) {
          selects[s].selectedIndex = o;
          selects[s].dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }

    return false;
  }

  /* ------------------------------------------------------- drawing a bar */

  function buildBars(scrim, data, selected) {
    var barsEl = $('[data-jsamp-bars]', scrim);
    var axisEl = $('[data-jsamp-axis]', scrim);
    var overEl = $('[data-jsamp-overcap]', scrim);
    if (!barsEl) return;

    barsEl.innerHTML = '';

    var base = data.baseLoad || 70;
    var max = 0;
    data.tiers.forEach(function (t) { if (t.amp > max) max = t.amp; });
    if (!max) return;

    data.tiers.forEach(function (t) {
      var isSel = t.amp === selected;
      var basePct = (base / max) * 100;
      var availPct = ((t.amp - base) / max) * 100;
      if (availPct < 0) availPct = 0;

      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'jsamp-row' + (isSel ? ' is-selected' : '');
      row.setAttribute('data-amp', t.amp);

      var readOut = t.amp + ' amp';
      if (t.price) readOut += ', ' + t.price;
      if (t.rms) readOut += ', supports up to ' + num(t.rms) + ' watts RMS';
      row.setAttribute('aria-label', readOut + '. Choose this amperage.');

      var rmsInside = t.rms
        ? '<span class="jsamp-rms">up to ' + num(t.rms) + 'W RMS</span>'
        : '';

      var html =
        '<span class="jsamp-row__tier">' +
          '<b>' + t.amp + 'A</b>' +
          (t.price ? '<span>' + t.price + '</span>' : '') +
        '</span>' +
        '<span class="jsamp-track">' +
          '<span class="jsamp-fill">' +
            '<span class="jsamp-base" style="width:' + basePct + '%"></span>' +
            '<span class="jsamp-avail" style="width:' + availPct + '%">' + rmsInside + '</span>' +
          '</span>' +
        '</span>';

      if (t.desc) {
        html += '<span class="jsamp-row__desc">' + t.desc + '</span>';
      }

      row.innerHTML = html;

      row.addEventListener('click', function () {
        selectAmpOnPage(t.amp, data);
        close(scrim);
      });

      barsEl.appendChild(row);
    });

    /* Labels that do not fit inside their own bar move underneath it.

       This is MEASURED, not estimated from the panel width. An earlier
       version guessed with a pixel threshold and got it wrong whenever the
       currency string, the font or the panel width was not what it assumed.
       Reading the real widths after the rows are in the document costs one
       layout pass and is right at any size, in any currency. */
    Array.prototype.forEach.call(barsEl.querySelectorAll('.jsamp-row'), function (row) {
      var availEl = row.querySelector('.jsamp-avail');
      var inside = availEl ? availEl.querySelector('.jsamp-rms') : null;
      if (!inside) return;

      if (inside.scrollWidth + 4 > availEl.clientWidth) {
        var outside = document.createElement('span');
        outside.className = 'jsamp-rms jsamp-rms--outside';
        outside.textContent = inside.textContent;
        var desc = row.querySelector('.jsamp-row__desc');
        if (desc) row.insertBefore(outside, desc);
        else row.appendChild(outside);
        inside.parentNode.removeChild(inside);
      }
    });

    if (axisEl) {
      axisEl.innerHTML =
        '<span>0A</span><span>' + Math.round(max / 2) + 'A</span><span>' + max + 'A</span>';
    }

    /* The "what if I need more than the top tier" note.

       JUSTIN'S RULE, and it is the whole reason this reads the way it does:
       NEVER point the customer at a different vehicle. His words, "someone
       shopping a Silverado has no use for a Sierra link". So when the page
       tops out below what they need we CAP at the top tier on this page, say
       so plainly, and offer the dual or triple alternator conversation.
       Nothing in here may ever say "try another model". */
    if (overEl) {
      var top = null;
      data.tiers.forEach(function (t) { if (t.amp === max) top = t; });
      var topRms = top && top.rms ? num(top.rms) : null;

      if (!topRms) {
        /* No published wattage for the top tier on this page, so there is no
           honest number to cap against. Say nothing rather than guess. */
        overEl.innerHTML = '';
        overEl.style.display = 'none';
      } else if (max >= (data.singleAltCeiling || 400)) {
        /* This page already reaches the biggest single alternator we build,
           so anything above it genuinely is a dual or triple setup. */
        overEl.style.display = '';
        overEl.innerHTML = '<b>Over ' + topRms + 'W RMS?</b> ' + (data.overTopText || '');
      } else {
        /* This page tops out below our biggest. Name the ceiling for THIS
           vehicle and stop there. */
        var whose = data.vehicle ? ('the ' + data.vehicle) : 'this vehicle';
        overEl.style.display = '';
        overEl.innerHTML =
          '<b>Need more than ' + topRms + 'W RMS?</b> ' +
          'The largest we build for ' + whose + ' is ' + max + ' amp. ' +
          (data.overPageText || '');
      }
    }
  }

  /* ------------------------------------------------------- open and close */

  var lastFocus = null;
  var lockedScrollY = 0;

  /* THE PAGE BEHIND MUST NOT SCROLL.

     On this theme the scrolling element is <html>, not <body>, so
     overflow:hidden on body alone did nothing at all. Locking both, and
     pinning body with position:fixed at a negative top, is the only
     combination that also holds on iOS Safari. The exact scroll position is
     remembered and restored on close, otherwise the page jumps to the top
     when the pop-up shuts. */
  function lockScroll() {
    lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);
    document.body.style.top = (-lockedScrollY) + 'px';
  }

  function unlockScroll() {
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  }

  /* The pop-up is written into the page inside the product form, which is a
     sensible place for it to live in the Liquid but a poor place for it to
     RENDER from. Everything in that part of the page is centre aligned and
     several wrappers use overflow:clip. Moving the overlay to the end of
     <body> the first time it opens takes it out of all of that, and out of
     the way of any future ancestor that would otherwise trap a fixed
     element, such as a transform or a filter. */
  function hoist(scrim) {
    if (scrim.parentElement !== document.body) {
      document.body.appendChild(scrim);
    }
  }

  function open(scrim) {
    var data = readData(scrim);
    if (!data) return;

    hoist(scrim);
    lastFocus = document.activeElement;
    scrim.classList.add(OPEN_CLASS);
    lockScroll();

    /* Drawn after the panel is visible so the bar widths are measured
       against the real panel, not a hidden one. */
    buildBars(scrim, data, currentAmp(data));

    var closeBtn = $('[data-jsamp-close]', scrim);
    if (closeBtn) closeBtn.focus();
  }

  function close(scrim) {
    scrim.classList.remove(OPEN_CLASS);
    unlockScroll();
    /* preventScroll keeps the browser from jumping back to the link we just
       restored focus to, which would undo the scroll position. */
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus({ preventScroll: true }); }
      catch (e) { lastFocus.focus(); }
    }
    lastFocus = null;
  }

  function openScrim() {
    return document.querySelector('.jsamp-scrim.' + OPEN_CLASS);
  }

  /* --------------------------------------------------------------- wiring */

  /* Delegated, so a pop-up added later by the theme editor or a quick-shop
     modal works without re-running any setup. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-jsamp-open]') : null;
    if (trigger) {
      e.preventDefault();
      var id = trigger.getAttribute('data-jsamp-open');
      var scrim = id ? document.getElementById(id) : document.querySelector('.jsamp-scrim');
      if (scrim) open(scrim);
      return;
    }

    var closer = e.target.closest ? e.target.closest('[data-jsamp-close]') : null;
    if (closer) {
      e.preventDefault();
      var s = closer.closest('.jsamp-scrim');
      if (s) close(s);
      return;
    }

    /* Tapping the dark area outside the panel closes it. */
    if (e.target.classList && e.target.classList.contains('jsamp-scrim')) {
      close(e.target);
    }
  });

  document.addEventListener('keydown', function (e) {
    var scrim = openScrim();
    if (!scrim) return;

    if (e.key === 'Escape' || e.keyCode === 27) {
      close(scrim);
      return;
    }

    /* Keeps keyboard focus inside the pop-up while it is open. */
    if (e.key === 'Tab' || e.keyCode === 9) {
      var f = $$('button, a[href], [tabindex]:not([tabindex="-1"])', scrim)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* Rotating the phone changes how much room a bar has for its label,
     so the bars are redrawn rather than left clipped. */
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    var scrim = openScrim();
    if (!scrim) return;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var data = readData(scrim);
      if (data) buildBars(scrim, data, currentAmp(data));
    }, 150);
  });

  /* Stage 2 hooks in here. For now the button is hidden by the snippet
     unless the stage 2 setting is switched on. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-jsamp-find]') : null;
    if (!btn) return;
    e.preventDefault();
    if (typeof window.jsAmperageStage2 === 'function') {
      window.jsAmperageStage2(btn.closest('.jsamp-scrim'));
    }
  });
})();